import React, { useState } from 'react'
import '../../styles/tickets-mobile.css'
import { useLongPress, formatRelativeTime } from './hooks.js'

function StripTooltip({ ticket, top }) {
  if (!ticket) return null
  return (
    <div
      className="tab-strip-tooltip"
      role="tooltip"
      style={{ top }}
    >
      <div className="tab-strip-tooltip__sub">{ticket.subject || '(No subject)'}</div>
      {ticket.customer && (
        <div className="tab-strip-tooltip__from">{ticket.customer}</div>
      )}
      {ticket.createdAt && (
        <div className="tab-strip-tooltip__age">{formatRelativeTime(ticket.createdAt)}</div>
      )}
    </div>
  )
}

function StripRow({ ticket, isActive, onSwitch, onPeek, onClearPeek }) {
  const longPress = useLongPress(() => onPeek?.())
  const handleClick = () => {
    if (longPress.didLongPress()) return
    onSwitch?.(ticket)
  }
  return (
    <button
      className={`tab-strip-row ${isActive ? 'tab-strip-row--active' : ''}`}
      aria-label={`Switch to ticket ${ticket.subject || '(No subject)'}`}
      aria-current={isActive ? 'true' : undefined}
      title={ticket.subject || '(No subject)'}
      onClick={handleClick}
      onMouseEnter={() => onPeek?.()}
      onMouseLeave={() => onClearPeek?.()}
      onPointerDown={longPress.onPointerDown}
      onPointerUp={longPress.onPointerUp}
      onPointerMove={longPress.onPointerMove}
      onPointerCancel={longPress.onPointerCancel}
    >
      <span className={`tab-strip-bar tab-strip-bar--${ticket.priority || 'normal'}`} aria-hidden />
    </button>
  )
}

// Row height (40px) + gap (2px) — matches `.tab-strip-row { min-height: 40px }`
// and `.tab-strip-vertical { gap: 2px }`. Also accounts for the » Expand
// button + its margin-bottom above the rows.
const STRIP_ROW_HEIGHT = 42
const STRIP_EXPAND_OFFSET = 46  // 32px button + 6px margin-bottom + 8px col padding

function PriorityStrip({ tickets, activeId, onSwitch }) {
  // peek tracks { id, index } — id for which ticket to show; index lets
  // the tooltip anchor vertically next to the row. Cleared on mouse-leave
  // or long-press release.
  const [peek, setPeek] = useState(null)
  const peekTicket = peek ? tickets.find(t => t.id === peek.id) : null
  const tooltipTop = peek ? STRIP_EXPAND_OFFSET + peek.index * STRIP_ROW_HEIGHT : 0

  return (
    <>
      <nav className="tab-strip-vertical" aria-label="Ticket queue">
        {tickets.map((t, index) => (
          <StripRow
            key={t.id}
            ticket={t}
            isActive={t.id === activeId}
            onSwitch={onSwitch}
            onPeek={() => setPeek({ id: t.id, index })}
            onClearPeek={() => setPeek(curr => (curr?.id === t.id ? null : curr))}
          />
        ))}
      </nav>
      <StripTooltip ticket={peekTicket} top={tooltipTop} />
    </>
  )
}

function EmptyState() {
  return (
    <div className="tab-empty">
      <div className="tab-empty-glyph" aria-hidden>📨</div>
      <div className="tab-empty-h">No ticket open</div>
      <div className="tab-empty-p">Tap a ticket on the left to read &amp; reply.</div>
    </div>
  )
}

/**
 * Tablet layout (700–1023px viewport, mobileV2 flag on).
 *
 * Three states:
 * 1. No ticket open → 2-col: inbox + empty placeholder in conversation column.
 * 2. Ticket open, not expanded → "focus mode": 40px priority strip + conversation + context.
 * 3. Ticket open, `expanded` true → 2-col: full inbox + conversation (no strip, no context).
 *    Lets the operator re-scan the queue while keeping the ticket open;
 *    clicking a different ticket from the expanded inbox resumes focus mode
 *    (the orchestrator clears `expanded` on selectTicket).
 *
 * State machine lives in the orchestrator. This component is pure layout.
 */
function TabletTicketsLayout({
  inbox, conversation, context,
  hasSelectedTicket, activeTicketId,
  stripTickets = [],
  expanded = false,
  onExpandInbox, onSwitchTicket
}) {
  // State 1 — no ticket open
  if (!hasSelectedTicket) {
    return (
      <div className="tab-layout">
        <aside className="tab-inbox-col">{inbox}</aside>
        <main className="tab-conv-col"><EmptyState /></main>
      </div>
    )
  }

  // State 3 — expanded back to inbox while keeping the ticket open
  if (expanded) {
    return (
      <div className="tab-layout">
        <aside className="tab-inbox-col">{inbox}</aside>
        <main className="tab-conv-col">{conversation}</main>
      </div>
    )
  }

  // State 2 — focus mode
  return (
    <div className="tab-layout tab-layout--focus">
      <aside className="tab-strip-col">
        <button
          className="tab-strip-expand"
          onClick={onExpandInbox}
          aria-label="Expand inbox"
          title="Expand inbox"
        >»</button>
        <PriorityStrip tickets={stripTickets} activeId={activeTicketId} onSwitch={onSwitchTicket} />
      </aside>
      <main className="tab-conv-col">{conversation}</main>
      <aside className="tab-ctx-col">{context}</aside>
    </div>
  )
}

export default TabletTicketsLayout
